import { useFusionAuth } from "@fusionauth/react-sdk";
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { debounce } from "@mui/material/utils";
import React, { useCallback, useEffect, useState } from "react";
import { useFetch } from "../../../services/hooks/useFetch.ts";
import { deviceApiPrefix, projectTypes } from "../../../utils/main.ts";

export default function DeviceDetails({
  displayContent,
  displayErrors,
  onUpdateData,
  projectData,
}: {
  displayContent: boolean;
  displayErrors: boolean;
  onUpdateData: (data: Partial<ProjectContextType>) => void;
  projectData: ProjectContextType;
}) {
  const { data: devices, fetch: fetchDevices } = useFetch<Device[], Device>();
  const { data: categories, fetch: fetchCategories } = useFetch<Gmdn[], Gmdn>();
  const { data: manufacturers, fetch: fetchManufacturers } = useFetch<
    DeviceManufacturer[],
    DeviceManufacturer
  >();
  const [searchTerm, setSearchTerm] = useState("");
  const { userInfo } = useFusionAuth();
  const isWorkshop = userInfo?.roles?.includes("WORKSHOP");

  const [projectType, setProjectType] = useState<number | undefined>(
    projectData?.type,
  );
  if (!projectData.deviceManufacturer) {
    projectData.deviceManufacturer =
      (projectData.devices && projectData.devices[0]?.deviceManufacturer) ||
      undefined;
  }

  useEffect(() => {
    if (!isWorkshop || !searchTerm) return;
    void fetchManufacturers(
      `${deviceApiPrefix}/devices/manufacturers?search=${
        "~" + searchTerm + "~"
      }`,
    );
  }, [isWorkshop, fetchManufacturers, searchTerm]);

  // eslint-disable-next-line
  const setSearchTermDelayed = useCallback(
    debounce((value: string) => {
      setSearchTerm(value);
    }, 500),
    [],
  );

  //Get categories by company (based on user)
  useEffect(() => {
    if (isWorkshop || projectType !== projectTypes.device) return;
    void fetchCategories(`${deviceApiPrefix}/categories/`);
  }, [isWorkshop, fetchCategories, projectType]);

  //Get categories by mfr
  useEffect(() => {
    if (!isWorkshop || !projectData.deviceManufacturer) return;
    void fetchCategories(
      `${deviceApiPrefix}/devices/manufacturers/${projectData.deviceManufacturer.id}/categories`,
    );
  }, [isWorkshop, projectData.deviceManufacturer, fetchCategories]);

  //Get devices by mfr and category
  useEffect(() => {
    if (
      !isWorkshop ||
      !projectData.deviceManufacturer ||
      !projectData.deviceGmdn
    )
      return;
    void fetchDevices(
      `${deviceApiPrefix}/devices/manufacturers/${projectData.deviceManufacturer.id}/categories/${projectData.deviceGmdn.id}`,
    );
  }, [
    isWorkshop,
    projectData.deviceManufacturer,
    projectData.deviceGmdn,
    fetchDevices,
  ]);

  //Get devices by company and category (based on user)
  useEffect(() => {
    if (isWorkshop || !projectData.deviceGmdn) return;
    void fetchDevices(
      `${deviceApiPrefix}/categories/${projectData.deviceGmdn.id}/devices`,
    );
  }, [isWorkshop, projectData.deviceGmdn, fetchDevices]);

  if (!displayContent) {
    return null;
  }

  return (
    <section className="modal-list">
      <Typography variant="body1">Select a Project Type</Typography>
      <FormControl fullWidth size="small">
        <InputLabel>Project Type</InputLabel>
        <Select
          label="Project Type"
          name="ProjectType"
          size="small"
          value={projectType || ""}
          variant="outlined"
          onChange={(event) => {
            const value = event.target.value;
            const manualCohort = true;
            setProjectType(value);
            onUpdateData({ type: value, isManualCohort: manualCohort });
          }}
          SelectDisplayProps={{
            style: { display: "flex", alignItems: "center", gap: "8px" },
          }}
        >
          <MenuItem value={projectTypes.device} key="projectType-device">
            Device-centric
          </MenuItem>
          <MenuItem value={projectTypes.noDevice} key="projectType-nodevice">
            Non Device-centric
          </MenuItem>
        </Select>
      </FormControl>

      {projectType && projectType === projectTypes.device && (
        <div id={"device-details-div"}>
          <Typography variant={"body2"} sx={{ marginBottom: "10px" }}>
            Select a category and device name from the list of your
            organization’s devices
          </Typography>

          <Typography
            variant={"body1"}
            sx={{ marginBottom: "7px", color: "text.secondary" }}
          >
            * Required fields
          </Typography>

          <div className="drawer-options">
            {isWorkshop && (
              <FormControl fullWidth margin="dense">
                <Autocomplete
                  filterOptions={(x) => x}
                  autoComplete
                  filterSelectedOptions
                  noOptionsText="Search for manufacturers"
                  options={manufacturers || []}
                  value={projectData.deviceManufacturer || {}}
                  onChange={(_event, value) =>
                    onUpdateData({
                      deviceManufacturer: value === null ? undefined : value,
                    })
                  }
                  onInputChange={(_, value) => setSearchTermDelayed(value)}
                  getOptionLabel={(option) => {
                    if (
                      option.name === undefined ||
                      option.dunsNumber === undefined
                    ) {
                      return "";
                    }
                    return option.name + " (" + option.dunsNumber + ")";
                  }}
                  getOptionKey={(option) => option.id}
                  data-testid="SelectCategoryAndDevice-autocompletemfr"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="medium"
                      variant="outlined"
                      label={"Device Manufacturer"}
                      slotProps={{
                        htmlInput: { ...params.inputProps },
                      }}
                    />
                  )}
                />
                {displayErrors && (
                  <FormHelperText error>This field is required</FormHelperText>
                )}
              </FormControl>
            )}

            <FormControl fullWidth margin="dense">
              <Autocomplete
                autoComplete
                filterSelectedOptions
                disabled={categories === null || categories?.length === 0}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                options={categories || []}
                value={projectData.deviceGmdn || null}
                getOptionLabel={(option) => option.name}
                getOptionKey={(option) => option.id}
                data-testid="SelectCategoryAndDevice-autocompletegmdn"
                onChange={(_event, value) =>
                  onUpdateData({
                    deviceGmdn: value === null ? undefined : value,
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="medium"
                    variant="outlined"
                    label={"Device Category"}
                    required
                    slotProps={{
                      htmlInput: { ...params.inputProps },
                    }}
                  />
                )}
              />
              {displayErrors && !projectData.deviceGmdn && (
                <FormHelperText error>This field is required</FormHelperText>
              )}
            </FormControl>

            <FormControl fullWidth margin="dense">
              <Autocomplete
                filterSelectedOptions
                multiple
                isOptionEqualToValue={(option, value) => option.id === value.id}
                options={devices || []}
                value={projectData.devices || []}
                getOptionLabel={(option) => option.description || ""}
                getOptionKey={(option) => option.id}
                data-testid="SelectCategoryAndDevice-autocompletedevice"
                onChange={(_event, value) => onUpdateData({ devices: value })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="medium"
                    variant="outlined"
                    label={"Device Description"}
                    required
                    slotProps={{
                      htmlInput: { ...params.inputProps },
                    }}
                  />
                )}
              />
              {displayErrors && (!devices || devices.length === 0) && (
                <FormHelperText error>This field is required</FormHelperText>
              )}
            </FormControl>
          </div>
        </div>
      )}
    </section>
  );
}
